import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/vue-query'
import { updateCommunityUserCache, transformToCommunityUser } from '@communities/community-user-layer/utils/cache-update.utils'
import { useAccessControl } from '@communities/access-control-layer/composables/useAccessControl'
import { leaderboardQueryKeys } from '@communities/leaderboard-layer/utils/query-keys.utils'
import { MembersService, type MemberDetailsParams } from '../services/members.service'
import { membersQueryKeys as queryKeys } from '../utils/query-keys.utils'
import type { CommunityUserGroup } from '../../community-user-layer/types/community-user'

const MEMBERS_LIST_CACHE_TIME_MS = 10 * 60 * 1000

/**
 * Members Query Store - Handles fetching member data using TanStack Query
 * Uses individual query creation functions for better scalability
 */
export const useMembersQueryStore = (groupId: MaybeRefOrGetter<string | undefined>) => {
  const service = new MembersService()
  const queryClient = useQueryClient()
  const accessControl = useAccessControl({
    resourceType: 'members',
    groupId,
    requireActiveMembership: true,
  })

  // ===== QUERY CREATION FUNCTIONS =====

  /**
   * Get group members by specific roles (e.g., ADMIN, OWNER) via the /list endpoint
   */
  const fetchGroupMembersListQuery = (
    groupId: MaybeRefOrGetter<string | undefined>,
    locationId: MaybeRefOrGetter<string | undefined>,
    filters?: MaybeRefOrGetter<string[]>,
    options?: { refetchOnMount?: boolean | 'always' }
  ) => {
    return useQuery<CommunityUserGroup[], Error>({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        const f = toValue(filters)
        return queryKeys.list(gId || '', lId || '', f || [])
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        const f = toValue(filters)
        if (!gId || !lId) {
          throw new Error('Group ID and Location ID are required')
        }
        return service.getGroupMembersList(gId, lId, { memberRole: f })
      },
      // Keeps admin/owner lists stable across route remounts while mutations
      // still invalidate member queries when roles change.
      staleTime: MEMBERS_LIST_CACHE_TIME_MS,
      gcTime: MEMBERS_LIST_CACHE_TIME_MS,
      refetchOnMount: options?.refetchOnMount,
      enabled: computed(() => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        return !!gId && !!lId && !accessControl.isChecking.value && accessControl.canAccess.value
      }),
    })
  }
  /**
   * Get count of pending member requests (for Requested tab badge).
   * When `fetchRequestCountEnabled` is false (e.g. viewer is not group admin), the query stays disabled and does not call GET .../users/request/count.
   */
  const fetchRequestCountQuery = (
    groupId: MaybeRefOrGetter<string | undefined>,
    locationId: MaybeRefOrGetter<string | undefined>,
    fetchRequestCountEnabled?: MaybeRefOrGetter<boolean>
  ) => {
    return useQuery<{ count: number }, Error>({
      queryKey: computed(() => queryKeys.requestCount(toValue(groupId) || '', toValue(locationId) || '')),
      queryFn: async () => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        if (!gId || !lId) {
          throw new Error('Group ID and Location ID are required')
        }
        return service.getGroupMemberRequestCount(gId, lId)
      },
      // Drives the Requested sidebar badge — keep it always fresh so newly
      // submitted join requests show up as soon as the page (or a sibling
      // filter) is re-entered, matching the list query's freshness policy.
      staleTime: 0,
      refetchOnMount: 'always',
      enabled: computed(() => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        const allowRequestCount = fetchRequestCountEnabled === undefined ? true : toValue(fetchRequestCountEnabled)
        return !!gId && !!lId && !accessControl.isChecking.value && accessControl.canAccess.value && allowRequestCount
      }),
    })
  }

  /**
   * Get group members by specific role (invited, banned, requested, etc.) with pagination
   */
  const fetchGroupMembersDetailsQuery = (
    groupId: MaybeRefOrGetter<string | undefined>,
    locationId: MaybeRefOrGetter<string | undefined>,
    params: MaybeRefOrGetter<MemberDetailsParams>
  ) => {
    return useQuery<unknown[], Error>({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const p = toValue(params)
        return queryKeys.details(gId || '', p)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        const p = toValue(params)
        if (!gId || !lId) {
          throw new Error('Group ID is required for fetching group members by role')
        }
        return service.getGroupMembersDetails(gId, lId, p)
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      enabled: computed(() => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        return !!gId && !!lId && !accessControl.isChecking.value && accessControl.canAccess.value
      }),
      refetchOnWindowFocus: false,
    })
  }
  /**
   * Fetch membership questions form query
   */
  const fetchMembershipQuestionsFormQuery = (
    groupId: MaybeRefOrGetter<string | undefined>,
    locationId: MaybeRefOrGetter<string | undefined>,
    formId: MaybeRefOrGetter<string>
  ) => {
    return useQuery({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const fId = toValue(formId)
        return queryKeys.membershipForm(gId, fId)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        const fId = toValue(formId)
        if (!gId || !lId || !fId) {
          throw new Error('Group ID, Location ID, and Form ID are required')
        }
        return service.fetchMembershipQuestionsForm(gId, lId, fId)
      },
      staleTime: 10 * 60 * 1000, // 10 minutes - forms don't change often
      enabled: computed(() => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        const fId = toValue(formId)
        return !!gId && !!lId && !!fId && !accessControl.isChecking.value && accessControl.canAccess.value
      }),
    })
  }
  /**
   * Infinite query for group members with server-side pagination
   */
  const fetchGroupMembersInfiniteQuery = (
    groupId: MaybeRefOrGetter<string | undefined>,
    locationId: MaybeRefOrGetter<string | undefined>,
    baseParams: MaybeRefOrGetter<Omit<MemberDetailsParams, 'skip' | 'limit'>>
  ) => {
    const infiniteQuery = useInfiniteQuery({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const p = toValue(baseParams)
        return queryKeys.infinite(gId, p)
      }),
      queryFn: async ({ pageParam = 0 }) => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        const p = toValue(baseParams)

        if (!gId || !lId) {
          throw new Error('Group ID and Location ID are required')
        }

        const params: MemberDetailsParams = {
          ...p,
          limit: 20,
          skip: pageParam,
        }

        const data = await service.getGroupMembersDetails(gId, lId, params)
        return data
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages, lastPageParam) => {
        if (!lastPage || lastPage.length < 20) {
          return undefined
        }
        const currentSkip = lastPageParam as number
        return currentSkip + 20
      },
      // Members list is high-churn (approvals, bans, role changes, new joins)
      // and the UX goal is always-fresh data on filter switches. `staleTime: 0`
      // marks cached pages as immediately stale; combined with
      // `refetchOnMount: 'always'`, every queryKey change (filter switch
      // re-subscribes the observer) triggers a background fetch while cached
      // pages render instantly.
      staleTime: 0,
      refetchOnMount: 'always',
      enabled: computed(() => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        return !!gId && !!lId && !accessControl.isChecking.value && accessControl.canAccess.value
      }),
    })
    const allData = computed(() => infiniteQuery.data?.value?.pages?.flat() ?? [])
    return { ...infiniteQuery, allData }
  }
  // ===== MUTATIONS =====

  /**
   * Update group member mutation
   */
  const updateGroupMemberMutation = useMutation<
    Record<string, unknown>,
    Error,
    { groupId?: string; locationId?: string; userId: string; role: string }
  >({
    mutationFn: async ({ groupId, locationId, userId, role }) => {
      if (!groupId || !locationId) {
        throw new Error('Group ID is required')
      }
      // Auth/access gate only; service call uses caller's intent so onSuccess cache update stays consistent with the API call.
      accessControl.assertCanCallApi('updating group member')

      return service.updateGroupMember(groupId, locationId, userId, role)
    },
    onSuccess: (data, { groupId }) => {
      // Invalidate member queries to refresh the data
      queryClient.invalidateQueries({
        queryKey: queryKeys.all(groupId),
        refetchType: 'active', // Only refetch currently active queries, not all cached queries
      })
    },
  })

  /**
   * Withdraw member invitation mutation
   */
  const withdrawMemberInvitationMutation = useMutation<
    Record<string, unknown>,
    Error,
    { groupId?: string; locationId?: string; userId: string; invitationId: string }
  >({
    mutationFn: async ({ groupId, locationId, userId, invitationId }) => {
      if (!groupId || !locationId) {
        throw new Error('Group ID is required')
      }
      accessControl.assertCanCallApi('withdrawing member invitation')
      return service.withdrawMemberInvitation(groupId, locationId, userId, invitationId)
    },
    onSuccess: (data, { groupId }) => {
      // Invalidate member queries to refresh the data
      queryClient.invalidateQueries({
        queryKey: queryKeys.all(groupId),
        refetchType: 'active', // Only refetch currently active queries, not all cached queries
      })
    },
  })

  /**
   * Approve member request mutation
   */
  const approveMemberRequestMutation = useMutation<
    Record<string, unknown>,
    Error,
    { groupId?: string; locationId?: string; userId: string; role?: string }
  >({
    mutationFn: async ({ groupId, locationId, userId, role = 'MEMBER' }) => {
      accessControl.assertCanCallApi('approving member request')
      if (!groupId || !locationId) {
        throw new Error('Group ID is required')
      }
      return service.approveMemberRequest(groupId, locationId, userId, role)
    },
    onSuccess: (_, { groupId: gId, locationId: lId }) => {
      if (gId && lId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.requestCount(gId, lId) })
      }
    },
    // Don't invalidate queries to prevent member removal from current filter view
    // The UI will handle the button state updates locally
  })

  /**
   * Reject member request mutation
   */
  const rejectMemberRequestMutation = useMutation<
    Record<string, unknown>,
    Error,
    { groupId?: string; locationId?: string; userId: string }
  >({
    mutationFn: async ({ groupId, locationId, userId }) => {
      accessControl.assertCanCallApi('rejecting member request')
      if (!groupId || !locationId) {
        throw new Error('Group ID is required')
      }
      return service.rejectMemberRequest(groupId, locationId, userId)
    },
    onSuccess: (_, { groupId: gId, locationId: lId }) => {
      if (gId && lId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.requestCount(gId, lId) })
      }
    },
    // Don't invalidate queries to prevent member removal from current filter view
    // The UI will handle the button state updates locally
  })

  /**
   * Submit membership answers mutation
   */
  const submitMembershipAnswersMutation = useMutation<
    CommunityUserGroup,
    Error,
    {
      groupId: string
      locationId?: string
      formId: string
      contactId: string
      answers: (string | boolean[])[]
    }
  >({
    mutationFn: async ({ groupId, locationId, formId, contactId, answers }) => {
      // Pending/requested members submit answers before becoming Active; do not use assertCanCallApi
      // (requireActiveMembership) here — server validates contactId/form.
      accessControl.assertAuthenticatedApiContext('submitting membership answers')
      if (!groupId || !locationId) {
        throw new Error('Group ID is required')
      }
      return service.submitMembershipAnswers(groupId, locationId, formId, contactId, answers)
    },
    onSuccess: (data, variables) => {
      const transformedData = transformToCommunityUser(data as unknown as Record<string, unknown>)
      const withMembershipAnswers: Partial<CommunityUserGroup> = {
        ...transformedData,
        membershipAnswers: {
          formId: variables.formId,
          answers: variables.answers,
        },
      }
      if (!variables.locationId) {
        return
      }
      return updateCommunityUserCache(queryClient, variables.groupId, variables.locationId, withMembershipAnswers)
    },
  })

  /**
   * Ban member mutation (optional deleteContent).
   */
  const banGroupMemberMutation = useMutation<
    Record<string, unknown>,
    Error,
    { groupId?: string; locationId: string; userId: string; deleteContent?: boolean }
  >({
    mutationFn: async ({ groupId, locationId, userId, deleteContent }) => {
      accessControl.assertCanCallApi('banning group member')
      if (!groupId) {
        throw new Error('Group ID is required')
      }
      return service.banGroupMember(groupId, locationId, userId, deleteContent)
    },
    onSuccess: (data, { groupId, userId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.all(groupId),
        refetchType: 'active', // Only refetch currently active queries, not all cached queries
      })
    },
  })

  /**
   * Unban member mutation
   */
  const unbanGroupMemberMutation = useMutation<Record<string, unknown>, Error, { groupId?: string; locationId: string; userId: string }>({
    mutationFn: async ({ groupId, locationId, userId }) => {
      accessControl.assertCanCallApi('unbanning group member')
      if (!groupId) {
        throw new Error('Group ID is required')
      }
      return service.unbanGroupMember(groupId, locationId, userId)
    },
    onSuccess: (data, { groupId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.all(groupId),
        refetchType: 'active', // Only refetch currently active queries, not all cached queries
      })
    },
  })

  /**
   * Remove member from group mutation (optional removeGamificationMeta when removing and wiping points).
   */
  const removeGroupMemberMutation = useMutation<
    Record<string, unknown>,
    Error,
    { groupId?: string; locationId: string; userId: string; removeGamificationMeta?: boolean }
  >({
    mutationFn: async ({ groupId, locationId, userId, removeGamificationMeta }) => {
      accessControl.assertCanCallApi('removing group member')
      if (!groupId) {
        throw new Error('Group ID is required')
      }
      return service.removeGroupMember(groupId, locationId, userId, removeGamificationMeta)
    },
    onSuccess: (_data, { groupId, removeGamificationMeta }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.all(groupId),
        refetchType: 'active', // Only refetch currently active queries, not all cached queries
      })
      if (removeGamificationMeta) {
        void queryClient.invalidateQueries({ queryKey: leaderboardQueryKeys.all(groupId) })
      }
    },
  })

  /**
   * Reset member gamification meta (leaderboard points) without removing from group.
   */
  const deleteMemberGamificationMetaMutation = useMutation<
    Record<string, unknown>,
    Error,
    { groupId?: string; locationId: string; userId: string }
  >({
    mutationFn: async ({ groupId, locationId, userId }) => {
      accessControl.assertCanCallApi('wiping member gamification meta')
      if (!groupId) {
        throw new Error('Group ID is required')
      }
      return service.deleteMemberGamificationMeta(groupId, locationId, userId)
    },
    onSuccess: (_data, { groupId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.all(groupId),
        refetchType: 'active',
      })
      void queryClient.invalidateQueries({ queryKey: leaderboardQueryKeys.all(groupId) })
    },
  })

  return {
    // Query creation functions
    fetchGroupMembersListQuery,
    fetchRequestCountQuery,
    fetchGroupMembersDetailsQuery,
    fetchMembershipQuestionsFormQuery,
    fetchGroupMembersInfiniteQuery,

    // Mutations
    updateGroupMemberMutation,
    withdrawMemberInvitationMutation,
    approveMemberRequestMutation,
    rejectMemberRequestMutation,
    submitMembershipAnswersMutation,
    banGroupMemberMutation,
    unbanGroupMemberMutation,
    removeGroupMemberMutation,
    deleteMemberGamificationMetaMutation,

    // Query keys for manual invalidation
    queryKeys,
  }
}
